import React from 'react';
import { Box, TextField, Button, Typography, Container } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(6, 4),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(12, 4),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(16, 4),
  },
  overflow: 'hidden',
  paddingBottom: 0,
}));

const MainHeading = styled(Typography)(({ theme }) => ({
  display: 'inline-block',
  background: 'linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.9) 90%, rgba(0, 0, 0, 0.7) 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontSize: '2rem',
  fontWeight: 'bold',
  textShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  animation: 'fadeIn 0.6s ease-out forwards',
  [theme.breakpoints.up('sm')]: {
    fontSize: '3rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '4rem',
    lineHeight: 1.2,
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '5rem',
  },
}));

const Tagline = styled(Typography)(({ theme }) => ({
  maxWidth: '32rem',
  fontWeight: 500,
  color: 'rgba(0, 0, 0, 0.7)',
  opacity: 0,
  animation: 'fadeIn 0.6s ease-out forwards',
  animationDelay: '150ms',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.125rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.25rem',
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 10,
  opacity: 0,
  animation: 'fadeIn 0.6s ease-out forwards',
  animationDelay: '300ms',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  width: '100%',
}));

const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  maxWidth: '28rem',
  gap: theme.spacing(1.5),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
    borderColor: 'rgba(33, 150, 243, 0.2)',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(180deg, #2196F3 0%, rgba(33, 150, 243, 0.8) 100%)',
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(180deg, rgba(33, 150, 243, 0.9) 0%, rgba(33, 150, 243, 0.7) 100%)',
  },
}));

const OrbContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  paddingTop: '3rem',
}));

const OrbEffect = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  paddingTop: '20%',
  '& .orb-main': {
    position: 'absolute',
    left: '-50%',
    top: 0,
    zIndex: 10,
    width: '200%',
    overflow: 'hidden',
    borderRadius: '100%',
    border: '4px solid #2196F3',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    paddingTop: '100%',
    boxShadow: `
      0px 0px 12px rgba(33, 150, 243, 0.8),
      0px 0px 64px rgba(30, 136, 229, 0.5),
      0px 0px 12px rgba(33, 150, 243, 0.8) inset
    `,
  },
  '& .orb-layer': {
    position: 'absolute',
    left: '-50%',
    top: 0,
    height: '200%',
    width: '200%',
    borderRadius: '100%',
    animation: 'pulseHover 8s ease-in-out infinite',
  },
  '& .orb-layer-1': {
    backgroundColor: 'rgba(30, 136, 229, 0.5)',
    maskImage: 'radial-gradient(140% 95%, transparent 0%, transparent 35%, black 55%)',
  },
  '& .orb-layer-2': {
    backgroundColor: 'rgba(33, 150, 243, 0.5)',
    maskImage: 'radial-gradient(140% 110%, transparent 0%, transparent 35%, black 55%)',
  },
  '& .orb-layer-3': {
    backgroundColor: 'white',
    top: '-5%',
    maskImage: 'radial-gradient(140% 120%, transparent 0%, transparent 38%, black 43%)',
  },
}));

const GradientEffects = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  top: '50%',
  '& .gradient-1': {
    position: 'absolute',
    left: '50%',
    height: '256px',
    width: '60%',
    transform: 'translate(-50%, -50%) scale(2.5)',
    borderRadius: '50%',
    background: 'radial-gradient(ellipse at center, rgba(30, 136, 229, 0.5) 10%, rgba(30, 136, 229, 0) 60%)',
    [theme.breakpoints.up('sm')]: {
      height: '512px',
    },
  },
  '& .gradient-2': {
    position: 'absolute',
    left: '50%',
    height: '128px',
    width: '40%',
    transform: 'translate(-50%, -50%) scale(2)',
    borderRadius: '50%',
    background: 'radial-gradient(ellipse at center, rgba(33, 150, 243, 0.3) 10%, rgba(30, 136, 229, 0) 60%)',
    [theme.breakpoints.up('sm')]: {
      height: '256px',
    },
  },
}));

// Add keyframe animations to the document
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeIn {
    0% { opacity: 0; transform: translateY(12px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulseHover {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-2%); }
  }
`;
document.head.appendChild(styleSheet);

const Waitlist = ({
  mainHeading = "Plan Your Dream Trip with AI-Powered Intelligence",
  tagline = "Join the waitlist for the smartest travel planner that creates personalized itineraries with local insights, real-time recommendations, and budget optimization.",
  buttonLabel = "Join Waitlist",
  buttonHref = "/waitlist",
  inputLabel = "Enter your email for early access",
  caption = "Be first to experience the future of travel planning • Priority access when we launch"
}) => {
  return (
    <StyledContainer>
      <Box sx={{ 
        position: 'relative',
        py: { xs: 4, md: 6, lg: 8 },
        px: 3,
        overflow: 'hidden'
      }}>
        <Container>
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 4, lg: 7 },
            textAlign: 'center'
          }}>
            <MainHeading variant="h1">
              {mainHeading}
            </MainHeading>

            <Tagline>
              {tagline}
            </Tagline>

            <FormContainer>
              <InputContainer>
                <StyledTextField
                  type="email"
                  placeholder={inputLabel}
                  variant="outlined"
                  fullWidth
                />
                <StyledButton
                  variant="contained"
                  href={buttonHref}
                >
                  {buttonLabel}
                </StyledButton>
              </InputContainer>
              <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                {caption}
              </Typography>
            </FormContainer>

            <OrbContainer>
              <OrbEffect>
                <Box className="orb-main">
                  <Box className="orb-layer orb-layer-1" />
                  <Box className="orb-layer orb-layer-2" />
                  <Box className="orb-layer orb-layer-3" />
                </Box>

                <GradientEffects>
                  <Box className="gradient-1" />
                  <Box className="gradient-2" />
                </GradientEffects>
              </OrbEffect>
            </OrbContainer>
          </Box>
        </Container>
      </Box>
    </StyledContainer>
  );
};

export default Waitlist;
